import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import {
  IngredientChoice,
  OrderedChoice,
  IngredientChoiceGroup,
  IngredientChoicesType,
  DeserializationUtils,
  OrderableItem,
  OrderedItem,
  OrderedItemService,
} from 'ngx-web-api';
import { UiOrchestratorService } from '../../core/services/ui-orchestrator.service';
import { HighlightElementsService } from '../../shared/highlight-elements.service';
import { TranslateService } from '@ngx-translate/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';

@Injectable({
  providedIn: 'root',
})
export class EditingItemChoicesService {
  private visibleChoice: BehaviorSubject<IngredientChoice | undefined> = new BehaviorSubject<IngredientChoice | undefined>(undefined);
  public visibleChoice$: Observable<IngredientChoice | undefined> = this.visibleChoice.asObservable();

  private visibleOrderedChoice: BehaviorSubject<OrderedChoice | undefined> = new BehaviorSubject<OrderedChoice | undefined>(undefined);
  public visibleOrderedChoice$: Observable<OrderedChoice | undefined> = this.visibleOrderedChoice.asObservable();

  private visibleChoiceGroup: BehaviorSubject<IngredientChoiceGroup | undefined> = new BehaviorSubject<IngredientChoiceGroup | undefined>(
    undefined
  );
  public visibleChoiceGroup$: Observable<IngredientChoiceGroup | undefined> = this.visibleChoiceGroup.asObservable();

  constructor(
    private uiOrchestrationService: UiOrchestratorService,
    private liveAnnouncer: LiveAnnouncer,
    private orderedItemService: OrderedItemService,
    private highlightService: HighlightElementsService,
    private translateService: TranslateService
  ) {}

  setVisibleChoice(choice?: IngredientChoice) {
    if (this.visibleChoice.value?.stepIndex && choice?.name === this.visibleChoice?.value?.name) {
      return;
    }

    if (!!choice) {
      this.liveAnnouncer.announce(`Choice ${choice?.name} has been selected`, 2000);
    }
    this.visibleChoice.next(DeserializationUtils.deserializeObj(choice, IngredientChoice));
    this.uiOrchestrationService.recalculateStepControlsPosition();
  }

  setVisibleOrderedChoice(choice?: OrderedChoice) {
    this.visibleOrderedChoice.next(DeserializationUtils.deserializeObj(choice, OrderedChoice));
    this.uiOrchestrationService.recalculateStepControlsPosition();
  }

  setVisibleChoiceGroup(choiceGroup?: IngredientChoiceGroup) {
    this.visibleChoiceGroup.next(DeserializationUtils.deserializeObj(choiceGroup, IngredientChoiceGroup));
    this.uiOrchestrationService.recalculateStepControlsPosition();
  }

  initSelectedChoiceGroup(choiceGroups: IngredientChoiceGroup[], hasListLayout = false) {
    if (choiceGroups?.length) {
      const groupIndex = hasListLayout ? choiceGroups.findIndex(c => c.isExpanded) : -1;
      this.setVisibleChoiceGroup(choiceGroups[groupIndex < 0 ? 0 : groupIndex]);
    } else {
      this.setVisibleChoiceGroup(undefined);
    }
  }

  getNextChoice(choices: IngredientChoice[]): IngredientChoice | undefined {
    const filteredChoices = choices.filter(c => !c.isInstructions);
    const currentChoice = this.visibleChoice.value;
    const choiceIndex = currentChoice ? filteredChoices.findIndex(c => c.name === currentChoice.name) : -1;
    if (choiceIndex > -1 && choiceIndex < filteredChoices.length - 1) {
      return filteredChoices[choiceIndex + 1];
    }
  }

  getNextChoiceGroup(choiceGroups: IngredientChoiceGroup[]): IngredientChoiceGroup | undefined {
    const selectedChoiceGroup = this.visibleChoiceGroup.getValue();
    const selectedChoiceGroupIndex = selectedChoiceGroup ? choiceGroups.findIndex(c => c.name === selectedChoiceGroup.name) : -1;

    if (selectedChoiceGroupIndex > -1 && selectedChoiceGroupIndex < choiceGroups.length - 1) {
      return choiceGroups[selectedChoiceGroupIndex + 1];
    }
  }

  getPreviousChoiceGroup(choiceGroups: IngredientChoiceGroup[]): IngredientChoiceGroup | undefined {
    const selectedChoiceGroup = this.visibleChoiceGroup.getValue();
    const selectedChoiceGroupIndex = selectedChoiceGroup ? choiceGroups.findIndex(c => c.name === selectedChoiceGroup.name) : -1;

    if (selectedChoiceGroupIndex > 0 && selectedChoiceGroupIndex < choiceGroups.length) {
      return choiceGroups[selectedChoiceGroupIndex - 1];
    }
  }

  isSelectedIngredientChoiceGroupValid(selectedIngredientChoiceGroup: IngredientChoiceGroup, visibleChoice: IngredientChoice) {
    return !!selectedIngredientChoiceGroup && !!visibleChoice && selectedIngredientChoiceGroup.choices.indexOf(visibleChoice.name) !== -1;
  }

  selectFirstInvalidChoice(orderable: OrderableItem, item: OrderedItem) {
    if (!orderable || !item) {
      return;
    }
    const invalidChoice = item.choices.find(c => !c.isValid());
    if (!invalidChoice) {
      return;
    }
    const invalidOrderableChoice = orderable.getChoice(invalidChoice.name);
    if (orderable.choiceGroups?.length) {
      const groupWithInvalid = orderable.choiceGroups.find(g => g.choices?.find(c => c === invalidChoice.name));
      if (!!groupWithInvalid) {
        this.setVisibleChoiceGroup(groupWithInvalid);
      }
    }
    this.setVisibleChoice(invalidOrderableChoice);
  }

  getSubmitBtnLabel(item: OrderedItem, hasTheme: boolean): string {
    if (item && item.itemId) {
      if (hasTheme) {
        return this.translateService.instant('component.item_editor.item_choices.done');
      }
      return this.translateService.instant('component.item_editor.item_choices.update');
    }
    return this.translateService.instant('component.item_editor.item_choices.add_to_order');
  }

  shouldShowSubmitBtn(
    orderableItem: OrderableItem,
    item: OrderedItem,
    ingredientChoicesType: IngredientChoicesType,
    isLastChoiceVisible: boolean
  ): boolean {
    return (
      isLastChoiceVisible ||
      this.orderedItemService.getFilteredIngredientChoices(orderableItem, item).length === 0 ||
      ingredientChoicesType !== IngredientChoicesType.Steps
    );
  }

  isItemValid(item: OrderedItem, orderableItem: OrderableItem): boolean {
    return this.orderedItemService.isValid(item, orderableItem);
  }

  handleInvalidSubmit(orderableItem: OrderableItem, item: OrderedItem, ingredientChoicesType: IngredientChoicesType): void {
    if (ingredientChoicesType !== IngredientChoicesType.Steps) {
      this.selectFirstInvalidChoice(orderableItem, item);
      this.highlightService.highLightAll();
    } else {
      this.highlightService.highLightAll(true);
    }
  }
}
